import { 
  UserDto, 
  CreateUserRequest, 
  UpdateUserRequest, 
  UserListResponse,
  UserActivityDto,
  BatchCreateUsersRequest,
  BatchCreateUsersResponse,
  UserMatchIdentifier,
  CreateUserMatchIdentifierRequest,
  UpdateUserMatchIdentifierRequest,
  UserMatchIdentifierListResponse,
  BatchMatchIdentifierRequest,
  BatchMatchIdentifierResponse,
  TestUserMatchIdentifierRequest,
  TestUserMatchIdentifierResponse,
  DivisionUserImportResult
} from '../types';
import { HttpClient } from './http-client';

export class DivisionUsersService {
  constructor(private httpClient: HttpClient) {}

  // User CRUD operations
  async getUsers(
    divisionId: string,
    params?: {
      active?: boolean;
      search?: string;
      matchIdentifierId?: string;
      matchIdentifierValue?: string;
      hasMatchIdentifiers?: boolean;
      limit?: number;
      offset?: number;
    }
  ): Promise<UserListResponse> {
    const queryParams = new URLSearchParams();
    
    if (params?.active !== undefined) {
      queryParams.append('active', params.active.toString());
    }
    if (params?.search) {
      queryParams.append('search', params.search);
    }
    if (params?.matchIdentifierId) {
      queryParams.append('matchIdentifierId', params.matchIdentifierId);
    }
    if (params?.matchIdentifierValue) {
      queryParams.append('matchIdentifierValue', params.matchIdentifierValue);
    }
    if (params?.hasMatchIdentifiers !== undefined) {
      queryParams.append('hasMatchIdentifiers', params.hasMatchIdentifiers.toString());
    }
    if (params?.limit) {
      queryParams.append('limit', params.limit.toString());
    }
    if (params?.offset) {
      queryParams.append('offset', params.offset.toString());
    }

    const response = await this.httpClient.get<UserListResponse>(
      `/api/v1/divisions/${divisionId}/users?${queryParams}`
    );
    return response;
  }

  async getUserById(divisionId: string, userId: string): Promise<UserDto> {
    const response = await this.httpClient.get<UserDto>(
      `/api/v1/divisions/${divisionId}/users/${userId}`
    );
    return response;
  }

  async createUser(divisionId: string, userData: CreateUserRequest): Promise<UserDto> {
    const response = await this.httpClient.post<UserDto>(
      `/api/v1/divisions/${divisionId}/users`,
      userData
    );
    return response;
  }

  async updateUser(
    divisionId: string, 
    userId: string, 
    userData: UpdateUserRequest
  ): Promise<UserDto> {
    const response = await this.httpClient.put<UserDto>(
      `/api/v1/divisions/${divisionId}/users/${userId}`,
      userData
    );
    return response;
  }

  async deleteUser(divisionId: string, userId: string): Promise<void> {
    await this.httpClient.delete(`/api/v1/divisions/${divisionId}/users/${userId}`);
  }

  async activateUser(divisionId: string, userId: string): Promise<{ id: string; activatedAt: string }> {
    return this.httpClient.post<{ id: string; activatedAt: string }>(
      `/api/v1/divisions/${divisionId}/users/${userId}/activate`,
      {}
    );
  }

  // User export functionality
  async exportUsers(
    divisionId: string,
    params?: {
      format?: 'csv' | 'json';
      active?: boolean;
      includeMatchIdentifiers?: boolean;
    }
  ): Promise<Blob> {
    const queryParams = new URLSearchParams();
    
    if (params?.format) {
      queryParams.append('format', params.format);
    }
    if (params?.active !== undefined) {
      queryParams.append('active', params.active.toString());
    }
    if (params?.includeMatchIdentifiers !== undefined) {
      queryParams.append('includeMatchIdentifiers', params.includeMatchIdentifiers.toString());
    }

    const response = await this.httpClient.get<Blob>(
      `/api/v1/divisions/${divisionId}/users/export?${queryParams}`,
      { responseType: 'blob' }
    );
    return response;
  }

  // User Match Identifier operations
  async getUserMatchIdentifiers(
    divisionId: string, 
    userId: string
  ): Promise<UserMatchIdentifierListResponse> {
    const response = await this.httpClient.get<UserMatchIdentifierListResponse>(
      `/api/v1/divisions/${divisionId}/users/${userId}/match-identifiers`
    );
    return response;
  }

  async assignMatchIdentifier(
    divisionId: string,
    userId: string,
    request: CreateUserMatchIdentifierRequest
  ): Promise<UserMatchIdentifier> {
    const response = await this.httpClient.post<UserMatchIdentifier>(
      `/api/v1/divisions/${divisionId}/users/${userId}/match-identifiers`,
      request
    );
    return response;
  }

  async updateUserMatchIdentifier(
    divisionId: string,
    userId: string,
    identifierId: string,
    request: UpdateUserMatchIdentifierRequest
  ): Promise<UserMatchIdentifier> {
    const response = await this.httpClient.put<UserMatchIdentifier>(
      `/api/v1/divisions/${divisionId}/users/${userId}/match-identifiers/${identifierId}`,
      request
    );
    return response;
  }

  async removeUserMatchIdentifier(
    divisionId: string,
    userId: string,
    identifierId: string
  ): Promise<void> {
    await this.httpClient.delete(
      `/api/v1/divisions/${divisionId}/users/${userId}/match-identifiers/${identifierId}`
    );
  }

  async batchAssignMatchIdentifiers(
    divisionId: string,
    userId: string,
    request: BatchMatchIdentifierRequest
  ): Promise<BatchMatchIdentifierResponse> {
    const response = await this.httpClient.post<BatchMatchIdentifierResponse>(
      `/api/v1/divisions/${divisionId}/users/${userId}/match-identifiers/batch`,
      request
    );
    return response;
  }

  // Test user match identifiers
  async testUserMatchIdentifiers(
    divisionId: string,
    userId: string,
    request: TestUserMatchIdentifierRequest
  ): Promise<TestUserMatchIdentifierResponse> {
    const response = await this.httpClient.post<TestUserMatchIdentifierResponse>(
      `/api/v1/divisions/${divisionId}/users/${userId}/match-identifiers/test`,
      request
    );
    return response;
  }

  // Note: User activity endpoint is not available in DivisionUsersController
  // Activity data would need to be implemented separately if needed
  async getUserActivity(
    divisionId: string,
    userId: string,
    params?: {
      startDate?: string;
      endDate?: string;
    }
  ): Promise<UserActivityDto> {
    // Return mock data since the endpoint doesn't exist
    return {
      userId,
      totalOrders: 0,
      pendingApprovals: 0,
      completedApprovals: 0,
      lastActivityAt: undefined,
      recentActivity: []
    };
  }

  // Batch operations
  async batchCreateUsers(
    divisionId: string,
    request: BatchCreateUsersRequest
  ): Promise<BatchCreateUsersResponse> {
    const response = await this.httpClient.post<BatchCreateUsersResponse>(
      `/api/v1/divisions/${divisionId}/users/batch`,
      request
    );
    return response;
  }

  // Import/Export operations
  async importDivisionUsers(
    divisionId: string,
    file: File,
    options: {
      format?: 'csv' | 'json';
      updateExisting?: boolean;
      includeMatchIdentifiers?: boolean;
    } = {}
  ): Promise<DivisionUserImportResult> {
    const formData = new FormData();
    formData.append('file', file);
    
    if (options.format) {
      formData.append('format', options.format);
    }
    formData.append('updateExisting', String(options.updateExisting ?? false));
    formData.append('includeMatchIdentifiers', String(options.includeMatchIdentifiers ?? false));

    const response = await this.httpClient.post<DivisionUserImportResult>(
      `/api/v1/divisions/${divisionId}/users/import`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response;
  }

  async exportDivisionUsers(
    divisionId: string,
    options: {
      format?: 'csv' | 'json';
      active?: boolean;
      includeMatchIdentifiers?: boolean;
    } = {}
  ): Promise<Blob> {
    const params = new URLSearchParams();
    if (options.format) params.append('format', options.format);
    if (options.active !== undefined) params.append('active', String(options.active));
    if (options.includeMatchIdentifiers !== undefined) {
      params.append('includeMatchIdentifiers', String(options.includeMatchIdentifiers));
    }

    return await this.httpClient.getBlob(
      `/api/v1/divisions/${divisionId}/users/export?${params.toString()}`
    );
  }

  // Generate CSV template for import
  generateImportTemplate(format: 'csv' | 'json' = 'csv'): string {
    if (format === 'csv') {
      return [
        'Email,FirstName,LastName,Address,City,State,ZipCode,Active,ExternalUserId,ExternalProvider,DivisionRoles,MatchIdentifiers',
        'john.doe@company.com,John,Doe,123 Main St,New York,NY,10001,True,EMP-001,,Approver,',
        'jane.smith@company.com,Jane,Smith,456 Oak Ave,Los Angeles,CA,90210,True,EMP-002,,Shopper,DoDAAC:M94700;Location:Building A'
      ].join('\n');
    } else {
      return JSON.stringify({
        users: [
          {
            email: 'john.doe@company.com',
            firstName: 'John',
            lastName: 'Doe',
            address: '123 Main St',
            city: 'New York',
            state: 'NY',
            zipCode: '10001',
            externalUserId: 'EMP-001',
            divisionRoles: [
              {
                divisionId: 'division-guid-here',
                role: 'Approver'
              }
            ],
            matchIdentifiers: []
          }
        ]
      }, null, 2);
    }
  }
}
